/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { SessionSlice } from '../../../../ts/state';
import * as crnUtil from '../../util/crn';
import * as types from './actionTypes';

const initialState: SessionSlice = {
  initialized: false,
  csrfToken: undefined,
  sessionInactive: false,
  sessionRevoked: false,
  ipAddressRestriction: false,
  checking: false,
  underReadOnlyView: false
};

const createSession = (state: SessionSlice, action: AnyAction) => {
  return {
    user: action.user,
    region: action.region,
    instance: action.instance,
    account: action.account,
    workspaceId: action.workspaceId,
    workspaceDetail: action.workspaceDetail,
    skillId: action.skillId,
    instanceState: action.instanceState,
    underReadOnlyView: action.underReadOnlyView,
    assistantId: action.assistantId,
    initialized: true
  };
};

const destroySession = (): SessionSlice => {
  return initialState;
};

/**
 * Action invoked before resetting the Redux store to "freeze" the application
 * (i.e. cause it to show a loading indicator) until the process is finished.
 * For instance, the URL of the page can be modified after the session has
 * been reset without triggering any routes to re-render before the process
 * is complete.
 *
 * @param state The session slice to reset
 * @returns The session slice
 */
const resetSession = (state: SessionSlice): SessionSlice => {
  // When resetting the session, retain only the values that are related to
  // the instance or the user: values related to assistants or skills should
  // be reset.
  const resetState: SessionSlice = _.omit(state, [
    'assistantId',
    'skillId',
    'workspaceDetail',
    'workspaceId'
  ]);

  return {
    ...resetState,
    // Indicate that the session is now not initialized so that the
    // application shows a loading screen until the Redux reset process
    // completes.
    initialized: false
  };
};

const setWorkspace = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    workspaceId: action.workspaceId,
    workspaceDetail: {
      ...state.workspaceDetail,
      workspace_id: action.workspaceId
    }
  };
};

const setWorkspaceDetail = (state: SessionSlice, action: AnyAction) => ({
  ...state,
  initialized: true,
  workspaceDetail: action.workspaceDetail
});

const setTenant = (state: SessionSlice, action: AnyAction) => {
  if (window._analytics?.commonProperties) {
    let instId = 'unset';
    if (action.instance) {
      if (crnUtil.isCrn(action.instance)) {
        instId = crnUtil.parse(action.instance).service_instance;
      }
    }
    window._analytics.commonProperties.instanceId = instId;
    window._analytics.commonProperties.instanceGuid = instId;
  }
  return {
    ...state,
    initialized: true,
    region: action.region,
    instance: action.instance
  };
};

const setUser = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    user: action.user
  };
};

const setAccount = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    account: action.account
  };
};

const setSkillId = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    skillId: action.skillId
  };
};

const setAssistantId = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    assistantId: action.assistantId
  };
};

const setInstanceState = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    instanceState: action.instanceState
  };
};

export const fetchUserStart = (state: SessionSlice) => {
  return {
    ...state,
    initialized: true,
    checking: true
  };
};

export const fetchUserSuccess = (state: SessionSlice, action: AnyAction) => {
  // Retrieve the CSRF token from the user object, but remove it from there
  // so that the value can be stored at the root of the session slice.
  const csrfToken = action.payload.csrfToken;
  delete action.payload.csrfToken;

  return {
    ...state,
    initialized: true,
    user: action.payload,
    csrfToken,
    checking: false
  };
};

export const fetchUserError = (state: SessionSlice) => {
  const newState = Object.assign({}, state, {
    checking: false,
    initialized: true
  });
  delete newState.user;
  return newState;
};

const setReadOnly = (state: SessionSlice, action: AnyAction) => {
  return {
    ...state,
    initialized: true,
    underReadOnlyView: action.underReadOnlyView
  };
};

const sessionInactive = (state: SessionSlice) => ({
  ...state,
  initialized: true,
  sessionInactive: true
});

const sessionRevoked = (state: SessionSlice) => ({
  ...state,
  initialized: true,
  sessionRevoked: true
});

const ipAddressRestriction = (state: SessionSlice) => ({
  ...state,
  initialized: true,
  ipAddressRestriction: true
});

export const reducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case types.CREATE_SESSION:
      return createSession(state, action);
    case types.DESTROY_SESSION:
      return destroySession();
    case types.RESET_SESSION:
      return resetSession(state);
    case types.SET_WORKSPACE:
      return setWorkspace(state, action);
    case types.SET_WORKSPACE_DETAIL:
      return setWorkspaceDetail(state, action);
    case types.SET_TENANT:
      return setTenant(state, action);
    case types.SET_USER:
      return setUser(state, action);
    case types.SET_ACCOUNT:
      return setAccount(state, action);
    case types.SET_SKILL_ID:
      return setSkillId(state, action);
    case types.SET_ASSISTANT_ID:
      return setAssistantId(state, action);
    case types.SET_INSTANCE_STATE:
      return setInstanceState(state, action);
    case types.SET_READ_ONLY:
      return setReadOnly(state, action);
    case types.FETCH_USER_START:
      return fetchUserStart(state);
    case types.FETCH_USER_SUCCESS:
      return fetchUserSuccess(state, action);
    case types.FETCH_USER_ERROR:
      return fetchUserError(state);
    case types.SESSION_INACTIVE:
      return sessionInactive(state);
    case types.SESSION_REVOKED:
      return sessionRevoked(state);
    case types.IP_ADDRESS_RESTRICTION:
      return ipAddressRestriction(state);
    default:
      return state;
  }
};

export default reducer;
